/**
 * HeroResourceUploadArea Component
 * Inline resource upload area for Hero page (landing)
 * Always visible below text input with drag & drop support
 */

import React, { DragEvent, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Upload, FileImage, FileText, File, X, AlertCircle, Folder, ChevronDown, ChevronUp, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { AgentProgress } from '../common/AgentProgress';

export interface StagedFile {
  id: string;
  file: File;
  category: 'images' | 'documents' | 'others';
  purpose: 'READ_UNDERSTAND' | 'ADD_TO_SCENES';
  savedName?: string;
}

export type UploadState = 'empty' | 'files_staged' | 'uploading' | 'transcribing' | 'analyzing' | 'error';

interface HeroResourceUploadAreaProps {
  /** Staged files waiting to be uploaded */
  files: StagedFile[];
  /** Callback when files are added */
  onFilesAdded: (files: File[]) => void;
  /** Callback when file is removed */
  onFileRemoved: (fileId: string) => void;
  /** Callback when file purpose is toggled */
  onPurposeChanged: (fileId: string, purpose: 'READ_UNDERSTAND' | 'ADD_TO_SCENES') => void;
  /** Current upload state */
  uploadState: UploadState;
  /** Upload progress percentage (0-100) */
  uploadProgress?: number;
  /** Analysis job ID for polling */
  analysisJobId?: string | null;
  /** Error message if any */
  error?: string;
  /** Callback when analysis completes */
  onAnalysisComplete?: () => void;
  /** Total file count for analysis estimation */
  uploadedFileCount?: number;
  /** Whether the upload area is expanded */
  isExpanded?: boolean;
  /** Callback to toggle expand state */
  onToggleExpand?: () => void;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
// Note: No file count limit when uploading directories - all files will be accepted

const getFileIcon = (file: File) => {
  const ext = file.name.split('.').pop()?.toLowerCase();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '')) {
    return <FileImage className="h-5 w-5 text-primary" />;
  }

  if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf', 'odt'].includes(ext || '')) {
    return <FileText className="h-5 w-5 text-green-500" />;
  }

  return <File className="h-5 w-5 text-gray-500" />;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const HeroResourceUploadArea: React.FC<HeroResourceUploadAreaProps> = ({
  files,
  onFilesAdded,
  onFileRemoved,
  onPurposeChanged,
  uploadState,
  uploadProgress = 0,
  analysisJobId,
  error,
  onAnalysisComplete,
  uploadedFileCount = 0,
  isExpanded = false,
  onToggleExpand,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const directoryInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Helper function to recursively read all files from a directory entry
  const readDirectoryRecursively = async (directoryEntry: any): Promise<File[]> => {
    const files: File[] = [];

    return new Promise((resolve) => {
      const directoryReader = directoryEntry.createReader();

      const readEntries = () => {
        directoryReader.readEntries(async (entries: any[]) => {
          if (entries.length === 0) {
            resolve(files);
            return;
          }

          for (const entry of entries) {
            if (entry.isFile) {
              const file = await new Promise<File>((resolveFile) => {
                entry.file(resolveFile);
              });
              files.push(file);
            } else if (entry.isDirectory) {
              const subFiles = await readDirectoryRecursively(entry);
              files.push(...subFiles);
            }
          }

          // Continue reading (directories may have more entries than fit in one read)
          readEntries();
        });
      };

      readEntries();
    });
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const allFiles: File[] = [];

    // Check if we have DataTransferItemList (supports directories)
    if (e.dataTransfer.items) {
      const items = Array.from(e.dataTransfer.items);

      for (const item of items) {
        const entry = item.webkitGetAsEntry();

        if (entry) {
          if (entry.isFile) {
            const file = await new Promise<File>((resolve) => {
              (entry as any).file(resolve);
            });
            allFiles.push(file);
          } else if (entry.isDirectory) {
            const dirFiles = await readDirectoryRecursively(entry);
            allFiles.push(...dirFiles);
          }
        }
      }
    } else {
      // Fallback for browsers that don't support DataTransferItemList
      const droppedFiles = Array.from(e.dataTransfer.files);
      allFiles.push(...droppedFiles);
    }

    if (allFiles.length > 0) {
      onFilesAdded(allFiles);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      onFilesAdded(Array.from(selectedFiles));
    }
    // Reset input value to allow selecting same file again
    e.target.value = '';
  };

  const handleDirectoryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      // When a directory is selected, all files come through e.target.files
      onFilesAdded(Array.from(selectedFiles));
    }
    // Reset input value to allow selecting same directory again
    e.target.value = '';
  };

  const handleBrowseFilesClick = () => {
    fileInputRef.current?.click();
  };

  const handleBrowseFolderClick = () => {
    directoryInputRef.current?.click();
  };

  // Show analysis progress
  if (uploadState === 'analyzing' && analysisJobId) {
    return (
      <div className="border rounded-lg bg-card p-4">
        <AgentProgress
          jobId={analysisJobId}
          showThinking={true}
          compact={false}
          estimatedDurationSeconds={uploadedFileCount * 30}
          fileCount={uploadedFileCount}
          onComplete={onAnalysisComplete}
          className="w-full"
        />
      </div>
    );
  }

  // Show upload progress
  if (uploadState === 'uploading') {
    return (
      <div className="border rounded-lg bg-card p-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Uploading {files.length} file{files.length !== 1 ? 's' : ''}...</span>
            <span className="text-muted-foreground">{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      </div>
    );
  }

  // Show transcribing state
  if (uploadState === 'transcribing') {
    return (
      <div className="border rounded-lg bg-card p-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="font-medium">Transcribing your audio...</span>
          </div>
          <p className="text-xs text-muted-foreground">
            This may take up to a minute depending on audio length.
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (uploadState === 'error' && error) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">Upload Failed</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty or files_staged state
  return (
    <div className="space-y-3">
      {/* Collapsed State - Compact trigger */}
      {!isExpanded && files.length === 0 && (
        <button
          type="button"
          onClick={onToggleExpand}
          onDragOver={(e) => {
            e.preventDefault();
            // Auto-expand on drag over
            onToggleExpand?.();
          }}
          className={cn(
            "w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl",
            "border border-dashed border-white/20 dark:border-white/10",
            "bg-white/30 dark:bg-white/5 backdrop-blur-sm",
            "text-sm studio-text-secondary hover:studio-text-primary",
            "hover:border-primary/30 hover:bg-primary/5",
            "transition-all duration-200 cursor-pointer"
          )}
        >
          <Paperclip className="h-4 w-4" />
          <span>Attach files or folders</span>
          <ChevronDown className="h-3.5 w-3.5 ml-1" />
        </button>
      )}

      {/* Expanded State - Full drop zone */}
      {(isExpanded || files.length > 0) && (
        <>
          {/* Collapse Header */}
          {files.length === 0 && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="w-full flex items-center justify-between py-1.5 px-2 text-xs studio-text-secondary hover:studio-text-primary transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" />
                Attachments
              </span>
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "border border-dashed rounded-xl p-5 text-center transition-all duration-200",
              "bg-white/30 dark:bg-white/5 backdrop-blur-sm",
              isDragOver
                ? "border-primary bg-primary/10"
                : "border-white/20 dark:border-white/10 hover:border-primary/30"
            )}
          >
            <Upload className={cn(
              "h-7 w-7 mx-auto mb-2.5",
              isDragOver ? "text-primary" : "text-muted-foreground"
            )} />
            <p className="text-sm font-medium mb-0.5 studio-text-primary">
              {isDragOver ? "Drop files or folders here" : "Drop files or folders here"}
            </p>
            <p className="text-xs studio-text-secondary mb-3">
              Maximum {formatFileSize(MAX_FILE_SIZE)} per file
            </p>

            {/* Browse Buttons */}
            <div className="flex items-center justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleBrowseFilesClick}
                className="text-xs h-8 bg-white/50 dark:bg-white/5 border-white/20 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10"
              >
                <File className="h-3 w-3 mr-1.5" />
                Browse Files
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleBrowseFolderClick}
                className="text-xs h-8 bg-white/50 dark:bg-white/5 border-white/20 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10"
              >
                <Folder className="h-3 w-3 mr-1.5" />
                Browse Folder
              </Button>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium studio-text-primary">
                  {files.length} file{files.length !== 1 ? 's' : ''} ready
                </p>
                {onToggleExpand && (
                  <button
                    type="button"
                    onClick={onToggleExpand}
                    className="text-xs studio-text-secondary hover:studio-text-primary flex items-center gap-1 transition-colors"
                  >
                    Collapse
                    <ChevronUp className="h-3 w-3" />
                  </button>
                )}
              </div>

              {files.map((stagedFile) => (
                <div
                  key={stagedFile.id}
                  className="flex items-center gap-3 p-3 bg-white/40 dark:bg-white/5 backdrop-blur-sm rounded-lg border border-white/20 dark:border-white/10"
                >
                  <div className="flex-shrink-0">
                    {getFileIcon(stagedFile.file)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate studio-text-primary">
                      {stagedFile.file.name}
                    </p>
                    <p className="text-xs studio-text-secondary">
                      {formatFileSize(stagedFile.file.size)} • {stagedFile.category}
                    </p>
                  </div>

                  {/* Purpose Toggle */}
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor={`purpose-${stagedFile.id}`}
                      className="text-xs cursor-pointer whitespace-nowrap studio-text-secondary"
                    >
                      {stagedFile.purpose === 'READ_UNDERSTAND' ? 'Read & Understand' : 'Add to Scenes'}
                    </Label>
                    <Switch
                      id={`purpose-${stagedFile.id}`}
                      checked={stagedFile.purpose === 'ADD_TO_SCENES'}
                      onCheckedChange={(checked) =>
                        onPurposeChanged(
                          stagedFile.id,
                          checked ? 'ADD_TO_SCENES' : 'READ_UNDERSTAND'
                        )
                      }
                    />
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onFileRemoved(stagedFile.id)}
                    className="flex-shrink-0 h-7 w-7 p-0 hover:bg-black/5 dark:hover:bg-white/10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Hidden Directory Input */}
      <input
        ref={directoryInputRef}
        type="file"
        /* @ts-ignore - webkitdirectory is not in standard types */
        webkitdirectory=""
        directory=""
        multiple
        className="hidden"
        onChange={handleDirectoryInputChange}
      />
    </div>
  );
};
